import type { Vec3 } from './assemblyPose';
import type { SlotArea } from './holeArea';
import { faceBasis, faceSizeAxes, type FaceWord, type SizeAxis } from './partFaces';
import { isLeftRight, type Side } from './partsRun';
import { holeOrigins, type HoleAt } from './stepHoles';
import { keptHand, swapFrom, swapHand, type HoleLine, type LineEnv } from './holeLines';
import { shownId } from './partsCatalogue';
import type { HardwareInteraction } from './hardwareInteractionsRun';
import type { SubStep } from './stepsRun';

// THE HOLES FIND HOLES ASKS FOR, NUMBERED BEFOREHAND — and everything about each that is already
// known, so the call is asked the one thing nobody knows: where on its face it goes.
//
// The hardware table says, of every joint, which part a piece of hardware enters, by which face,
// for which hand, how many times, and which barrel nut it threads into. The call used to be asked
// all of that again, field by field, beside the place — eight fields a line, two of them unknown.
// Now each hole the table lists where hardware ENTERS its first part is a slot, filled here and
// numbered as the hole it will be (H101, H102…, from the group's first free id), and the answer is
// a place per slot (see slotLines, which turns them back into the lines the rest of the run is
// built on). How deep a screw's head is sunk is not asked: it is worked out on the meshes (see
// holesOfLine).
//
// AND WHERE IT CAN GO. A screw through the frame into the end of a rail goes in where the rail's
// end touches the frame: the parts stand where the product built them (see lineEnv), so that patch
// is measured — first the overlap of the two parts' boxes, seen along the hole, then, on their
// meshes, the part of it where they really touch (see holeArea) — and handed over with the slot. A
// place estimated off a picture of a 482 mm frame came out 100 mm from its edge, in the open frame;
// inside a 24 × 44 mm patch it cannot.

// A RANGE ACROSS A FACE, in the hole's own words: millimetres from the edge it is counted from.
export type PatchRange = { from: FaceWord; lo: number; hi: number };

export type HoleSlot = {
  // THE ENTRY HOLE'S OWN ID, given before it is asked — the one it keeps once placed (see lineHoles).
  id: string;
  // THE LINE AS IT WILL BE, bar the place — by stored id; shown ids only once it is a line (see
  // slotLines), as an answered line's are, so it goes through the same path into holes.
  at: string;
  via: string;
  part: string;
  side?: Side;
  face: FaceWord;
  nut: HoleLine['nut'];
  // The part the hardware goes on into, where the table names one.
  next: string | null;
  // WHERE THE NEXT PART TOUCHES THE FACE — the two boxes' overlap here, narrowed to where the meshes
  // meet once the solids are loaded (see holeArea). Null where either part has no place in the
  // product, or the two do not meet across it: then the call is told so, and places it from the drawing.
  patch: { u: PatchRange; v: PatchRange } | null;
  // AND THE PLACES IN IT WHERE THE LINE WORKS (see holeArea) — absent until worked out, null where
  // it could not be.
  area?: SlotArea | null;
  // Which two axes the face runs along — the keys the answer gives its figures under.
  axes: { u: SizeAxis; v: SizeAxis };
  // THE TABLE'S IDS FOR THE LINE'S OTHER HOLES — see HoleLine.ids.
  ids: NonNullable<HoleLine['ids']>;
  // THE OTHER HAND'S SLOT, MADE FROM THIS ONE, where the part is a mirror pair asked for both hands
  // (see foldMirrors): its own id, gesture and nut — its place is this one's, reflected.
  mirror?: { id: string; at: string; side: Side; nut: HoleLine['nut']; ids: NonNullable<HoleLine['ids']> };
};

type Box = { min: Vec3; max: Vec3 };

// A PAIR'S UNIT FOR A HAND: the kept file where it is that hand, else that file reflected across
// the product's middle. A part that is not a pair is itself.
function unitBox(env: LineEnv, part: string, side: Side | undefined): Box | null {
  const box = env.placed.get(part);
  if (!box) return null;
  const mx = env.mirrorX;
  if (!side || !isLeftRight(env.items[Number(part)]) || mx == null || keptHand(box, mx) === side) return box;
  return { min: [2 * mx - box.max[0], box.min[1], box.min[2]], max: [2 * mx - box.min[0], box.max[1], box.max[2]] };
}

// THE OVERLAP OF `other` ACROSS `face` OF `box`, in that face's own words — each axis counted from
// the edge a hole on it is counted from (see holeOrigins and sayOn). Null where they miss.
function patchOn(box: Box, other: Box, face: FaceWord): { u: PatchRange; v: PatchRange } | null {
  const { u, v } = faceBasis(face);
  const [uFrom, vFrom] = holeOrigins(face);
  const range = (axis: Vec3, from: FaceWord): PatchRange | null => {
    const k = axis.findIndex((c) => c !== 0);
    const lo = Math.max(box.min[k], other.min[k]);
    const hi = Math.min(box.max[k], other.max[k]);
    if (hi - lo < 1) return null;
    const span = box.max[k] - box.min[k];
    const centre = (box.min[k] + box.max[k]) / 2;
    // Distance from the origin edge: along the axis, from the centre, plus half the span.
    const mm = (w: number) => Math.round(((w - centre) * axis[k] + span / 2) * 2) / 2;
    const [a, b] = [mm(lo), mm(hi)].sort((p, q) => p - q);
    return { from, lo: a, hi: b };
  };
  const ru = range(u, uFrom);
  const rv = range(v, vFrom);
  return ru && rv ? { u: ru, v: rv } : null;
}

// EVERY SLOT OF A GROUP, in the table's order: per row, per piece of boring hardware, the holes it
// enters by — its through holes, or, where it has none (a dowel, a screw into one part), its first
// blind one — `count` of each. A row with no holes listed (a table from before they were asked)
// gives none, and the group is then not asked: see stepHolesRun.
export function holeSlots(
  rows: HardwareInteraction[],
  subs: SubStep[],
  // THE ROW EACH SUB-STEP IS, by position in `subs` (see drillGroupParts) — what says which
  // gesture puts in which slot's hardware.
  rowOf: (HardwareInteraction | null)[],
  // The hardware each sub-step brings, by stored id.
  bringsOf: string[][],
  env: LineEnv | null,
  // The group's first free hole id: "H101" — only for a hole the table gave none (an answer from
  // before the ids were asked), so it is past every id the table gave.
  first: string,
): HoleSlot[] {
  let spare = Number(first.slice(1));
  const slots: HoleSlot[] = [];
  for (const row of rows) {
    const side = row.side ?? undefined;
    // THE GESTURES OF THIS ROW BRINGING A PIECE OF HARDWARE, in manual order — the n-th slot of it
    // is put in at the n-th, or at the last where the drawing shows fewer gestures than holes.
    const atOf = (hw: string) => {
      const letters = subs.filter((_, i) => rowOf[i] === row && bringsOf[i].includes(hw)).map((s) => s.letter);
      return (n: number) => letters[Math.min(n, letters.length - 1)] ?? subs[0]?.letter ?? '';
    };
    const vias = [...new Set(row.holes.filter((h) => h.kind === 'through' || h.kind === 'blind').map((h) => h.for))];
    for (const via of vias) {
      const mine = row.holes.filter((h) => h.for === via && (h.kind === 'through' || h.kind === 'blind'));
      const through = mine.filter((h) => h.kind === 'through');
      const entries = through.length ? through : mine.slice(0, 1);
      const at = atOf(via);
      const cross = row.holes.find((h) => h.kind === 'cross' && (h.crosses == null || h.crosses === via));
      const nutAt = cross ? atOf(cross.for) : null;
      let n = 0;
      // THE LINES THIS HARDWARE MAKES BEYOND ITS WAY IN — on into the next part, the nut across it —
      // whose n-th id is the n-th slot's.
      const beyond = [
        ...mine.filter((h) => !entries.includes(h)).map((h) => ({ h, via })),
        ...(cross ? [{ h: cross, via: cross.for }] : []),
      ];
      for (const entry of entries) {
        const face = entry.face as FaceWord;
        if (!['front', 'back', 'left', 'right', 'top', 'bottom'].includes(face)) continue;
        const next = entry.aligned ?? null;
        const own = env ? unitBox(env, entry.part, side) : null;
        const far = env && next ? unitBox(env, next, side) : null;
        const entryIds = entry.ids ?? [];
        for (let k = 0; k < entry.count; k++, n++) {
          const ids = beyond.flatMap(({ h, via: v }) => {
            const id = h.ids?.[n];
            return id ? [{ part: h.part, via: v, id }] : [];
          });
          slots.push({
            id: entryIds[k] ?? `H${spare++}`,
            ids,
            at: at(n),
            via,
            part: entry.part,
            ...(side ? { side } : {}),
            face,
            nut: cross && nutAt ? { via: cross.for, at: nutAt(n), face: cross.face as FaceWord } : null,
            next,
            patch: own && far ? patchOn(own, far, face) : null,
            axes: faceSizeAxes(face),
          });
        }
      }
    }
  }
  return env ? foldMirrors(slots, env) : slots;
}

// A MIRROR PAIR IS ONE FILE, SO ITS TWO HANDS ARE ASKED ONCE. The other rail is the kept file
// reflected, so every hole either hand is given is cut into that one file: the call placed the
// right rail's screws at the same distance from the product's left as the left rail's, and the file
// was drilled at four spots, two of them 2 mm apart — one hole with a crescent beside it. So the
// other hand's slot goes, and the kept hand's slot makes its line too, reflected (see slotLines).
// A slot of the other hand with no kept-hand twin (the table lists the two differently) is asked.
function foldMirrors(slots: HoleSlot[], env: LineEnv): HoleSlot[] {
  const keptOf = (part: string) => {
    const box = env.placed.get(part);
    return box && env.mirrorX != null ? keptHand(box, env.mirrorX) : null;
  };
  const out = slots.map((sl) => ({ ...sl }));
  const gone = new Set<HoleSlot>();
  for (const other of out) {
    const kept = keptOf(other.part);
    if (!other.side || !kept || other.side === kept) continue;
    const twin = out.find((sl) => sl.side === kept && !sl.mirror && !gone.has(sl) && sl.via === other.via
      && sl.part === other.part && sl.next === other.next && sl.face === swapHand(other.face));
    if (!twin) continue;
    twin.mirror = { id: other.id, at: other.at, side: other.side, nut: other.nut, ids: other.ids };
    gone.add(other);
  }
  return out.filter((sl) => !gone.has(sl));
}

// AN ANSWERED PLACE, as the server passes it on: the figures under the axes they are about.
// A BARE NUMBER is counted from the corner of the slot's outline (its patch), as the prompt asks
// where there is one; an edge and a figure, from the part's own edge.
export type SlotPlace = {
  id: string;
  width?: HoleAt | number; depth?: HoleAt | number; height?: HoleAt | number;
};

// WHICH AXIS AN EDGE BOUNDS — the edge, not the key, is the authority (see the server's
// _hole_axes): a figure filed under the wrong key still says which way it is counted.
const EDGE_AXIS: Record<FaceWord, SizeAxis> = {
  left: 'width', right: 'width', front: 'depth', back: 'depth', top: 'height', bottom: 'height',
};

// THE SLOTS, ANSWERED, AS LINES — the same lines an answer used to give in full, so everything
// after (holesOfLine, the drill, the checks) is unchanged. A slot whose place is missing or does
// not run across its face makes no line, and is said in `missing`.
export function slotLines(slots: HoleSlot[], places: SlotPlace[]): { lines: HoleLine[]; missing: string[] } {
  const lines: HoleLine[] = [];
  const missing: string[] = [];
  for (const slot of slots) {
    const said = places.find((p) => p.id.toUpperCase() === slot.id);
    const pick: Partial<Record<SizeAxis, HoleAt>> = {};
    // INSIDE THE OUTLINE: a number under one of the face's two axes, from the outline's corner — the
    // outline's own start added back, which is the place on the part.
    const { patch } = slot;
    if (said && patch) {
      for (const [axis, range] of [[slot.axes.u, patch.u], [slot.axes.v, patch.v]] as const) {
        const n = said[axis];
        if (typeof n === 'number' && Number.isFinite(n)) pick[axis] = { from: range.from, mm: Math.round((range.lo + n) * 2) / 2 };
      }
    }
    const given = said
      ? ([said.width, said.depth, said.height].filter((x) => x != null && typeof x !== 'number') as HoleAt[]) : [];
    const centred: HoleAt[] = [];
    for (const at of given) {
      if ('from' in at) {
        const axis = EDGE_AXIS[at.from];
        if (pick[axis]) continue;
        if ((axis === slot.axes.u || axis === slot.axes.v) && !pick[axis]) pick[axis] = at;
      } else centred.push(at);
    }
    for (const axis of [slot.axes.u, slot.axes.v]) if (!pick[axis] && centred.length) pick[axis] = centred.shift();
    const u = pick[slot.axes.u];
    const v = pick[slot.axes.v];
    if (!u || !v) {
      missing.push(slot.id, ...(slot.mirror ? [slot.mirror.id] : []));
      continue;
    }
    lines.push({
      id: slot.id,
      at: slot.at, via: shownId(slot.via), part: shownId(slot.part), ...(slot.side ? { side: slot.side } : {}),
      face: slot.face, u, v, nut: slot.nut ? { ...slot.nut, via: shownId(slot.nut.via) } : null,
      ids: slot.ids,
    });
    // AND THE OTHER HAND'S, its mirror image: said as an answer for that hand would be, which
    // lineHoles turns back into the kept file's words — so both land on the one spot of the file.
    const m = slot.mirror;
    if (m) {
      lines.push({
        id: m.id, at: m.at, via: shownId(slot.via), part: shownId(slot.part), side: m.side,
        face: swapHand(slot.face), u: swapFrom(u), v: swapFrom(v), nut: m.nut ? { ...m.nut, via: shownId(m.nut.via) } : null,
        ids: m.ids,
      });
    }
  }
  return { lines, missing };
}

// THE SLOTS IN RUNS, those sharing everything but their number — "H101, H102" — one line of the prompt
// and one pair of pictures each (see jointShots).
export const slotRuns = (slots: HoleSlot[]): HoleSlot[][] => {
  const key = (sl: HoleSlot) => JSON.stringify([sl.via, sl.part, sl.side, sl.face, sl.next, sl.patch]);
  const runs: HoleSlot[][] = [];
  for (const sl of slots) {
    const last = runs[runs.length - 1];
    if (last && key(last[0]) === key(sl)) last.push(sl);
    else runs.push([sl]);
  }
  return runs;
};
export const runLabel = (run: HoleSlot[]) => run.map((s) => s.id).join(', ');

// A PATCH IN THE PROMPT'S WORDS: "26–50 mm from the left, 362–406 mm from the bottom".
export const patchWords = (p: { u: PatchRange; v: PatchRange }) =>
  [p.u, p.v].map((r) => `${r.lo}–${r.hi} mm from the ${r.from}`).join(', ');
